import { Match } from "model/Match/Match";
import { MatchmakingSet } from "model/Match/Set/Matchmaking/MatchmakingSet";
import { getResults } from "model/Results";
import { Round } from "model/Tournament/Segment/RoundsSegmentGroup";
import { onlyUnique } from "Utility";

export type MmSwissRecord = {
  rosterId: string;
  wins: number;
  loses: number;
  matches: (Match | undefined)[];
  opponentIds: string[];
  buchholz: number;
  position: number;
  roundsWon: number;
  roundsLost: number;
};

const createSwissRecord = (
  rosterId: string,
  numberOfRounds: number
): MmSwissRecord => {
  return {
    rosterId,
    wins: 0,
    loses: 0,
    matches: Array<Match | undefined>(numberOfRounds),
    opponentIds: [],
    buchholz: 0,
    position: 0,
    roundsWon: 0,
    roundsLost: 0,
  };
};

// todo - bad temp hardcoded solution
export const calculateMmSwissRecords = (
  rosterIds: string[],
  rounds: Round[],
  matches: Match[]
) => {
  const records = new Map<string, MmSwissRecord>();
  rosterIds.forEach((id) => {
    records.set(id, createSwissRecord(id, rounds.length));
  });

  matches.forEach((m) => {
    const results = getResults(m.finalPositions, m.numWinners);
    if (!!results) {
      const rosterIds = Object.keys(results);
      const roundNum =
        rounds.find((r) => r.matchIds.includes(m.id))?.roundNum ?? -1;

      const tracks = m.sets.flatMap((s) => (s as MatchmakingSet).tracks);

      const winnerIds = rosterIds.filter((id) => results[id] === "W");
      const loserIds = rosterIds.filter((id) => results[id] === "L");
      winnerIds.forEach((winnerId) => {
        const winnerRecord = records.get(winnerId)!;
        winnerRecord.matches[roundNum] = m;
        winnerRecord.wins++;
        for (var v = 0; v < tracks.length; v++) {
          const trackScores = tracks[v].scores;
          winnerRecord.roundsWon += trackScores[winnerId];
          winnerRecord.roundsLost += trackScores[loserIds[0]];
        }

        winnerRecord.opponentIds.push(...loserIds);
      });

      loserIds.forEach((loserId) => {
        const loserRecord = records.get(loserId)!;
        loserRecord.matches[roundNum] = m;
        loserRecord.loses++;
        for (var v = 0; v < tracks.length; v++) {
          const trackScores = tracks[v].scores;
          loserRecord.roundsWon += trackScores[loserId];
          loserRecord.roundsLost += trackScores[winnerIds[0]];
        }

        loserRecord.opponentIds.push(...winnerIds);
      });
    }
  });

  const teamRecords = Array.from(records.values());
  teamRecords.forEach((r) => {
    r.opponentIds.filter(onlyUnique).forEach((opponentId) => {
      const oR = records.get(opponentId);
      if (!!oR) {
        r.buchholz += oR.wins - oR.loses;
      }
    });
  });

  const sortedRecords = teamRecords.sort(compareRecords);
  sortedRecords[0].position = 1;
  for (let i = 1; i < sortedRecords.length; i++) {
    if (compareRecords(sortedRecords[i - 1], sortedRecords[i]) === 0) {
      sortedRecords[i].position = sortedRecords[i - 1].position;
    } else {
      sortedRecords[i].position = i + 1;
    }
  }

  return teamRecords;
};

const compareRecords = (a: MmSwissRecord, b: MmSwissRecord): number => {
  if (a.wins > b.wins) {
    return -1;
  }

  if (b.wins > a.wins) {
    return 1;
  }

  if (a.loses < b.loses) {
    return -1;
  }

  if (b.loses < a.loses) {
    return 1;
  }

  if (a.buchholz > b.buchholz) {
    return -1;
  }

  if (b.buchholz > a.buchholz) {
    return 1;
  }

  if (a.roundsWon - a.roundsLost < b.roundsWon - b.roundsLost) {
    return 1;
  }

  if (b.roundsWon - b.roundsLost < a.roundsWon - a.roundsLost) {
    return -1;
  }

  if (a.roundsWon < b.roundsWon) {
    return 1;
  }

  if (b.roundsWon < a.roundsWon) {
    return -1;
  }

  return 0;
};
